import { useEffect, useRef, useState } from "react";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { GridActionsCellItem } from "@mui/x-data-grid";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import {
  useDeleteSavingsScheme,
  useGetSavingsScheme,
  useGetSavingsSchemeSearch,
} from "../../../../services/savings-scheme.service";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import SavingsModalForm from "../modals/SavingsModalForm";
import {
  APPLY_SAVINGS_CONTRIBUTION_ON,
  SavingsSchemeDto,
} from "../../../../models/savings-scheme.dto";
import { AiOutlinePlus } from "react-icons/ai";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import {
  useManageTableWithSearchParams,
  useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { useGetEmployeeBand } from "../../../../services/employee-band.service";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import APISearchInput from "../../../../components/search/APISearchInput";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import { EmployeeBandOrderBy } from "../../../../models/employee-band.dto";
import { useGetTaxReliefType } from "../../../../services/tax-relief-type.service";
import { TaxReliefTypeOrderBy } from "../../../../models/tax-relief-type.dto";
import {
  PermissionChecker,
  PERMISSIONS,
  useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import { savingsColumns, savingsColumnVisibility } from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import useUserStore from "../../../../state-management/useUserStore";

const SavingsView = () => {
  const { currentCompany } = useCompanyStore();
  const { checkUserPermissionsAnd } = useCheckUserPermissions();
  const { currentUser, userType } = useUserStore();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isEditForm, setIsEditForm] = useState(false);
  const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
    useColumnVisibilityModel(savingsColumnVisibility);

  const { mutate: searchSavingsSchemes } = useGetSavingsSchemeSearch();
  const {
    tableAPIRef,
    fetchedTableData,
    tableData,
    isLoading,
    pageCount,
    setTableData,
  } = useManageTableWithSearchParams<SavingsSchemeDto>({
    fetchHook: useGetSavingsScheme,
    options: { companyId: currentCompany?.id as number },
  });

  // Fetched data for select fields requiring data from database.
  // currentCompany can still be null on first render (it hasn't hydrated
  // from the store yet), so `currentCompany!.id` — a compile-time-only
  // assertion that does nothing at runtime — was throwing
  // "Cannot read properties of null (reading 'id')" here. Using `?.`
  // instead means these hooks just run with companyId/countryId as
  // undefined for that first render (the underlying query hooks handle an
  // undefined id) rather than crashing the component outright.
  const { data: employees } = useGetEmployee({
    companyId: currentCompany?.id as number,
    limit: APP_CONFIG.PAGE_LIMIT,
    status: "ACTIVE",
    orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
  });
  const { data: payPeriods } = useGetPayPeriod({
    companyId: currentCompany?.id as number,
    limit: APP_CONFIG.PAGE_LIMIT,
    orderBy: "createdAt:desc" as any,
  });
  const { data: employeeBands } = useGetEmployeeBand({
    companyId: currentCompany?.id as number,
    limit: APP_CONFIG.PAGE_LIMIT,
    orderBy: EmployeeBandOrderBy.NAME_ASC,
  });
  const { data: taxReliefTypes } = useGetTaxReliefType({
    countryId: currentCompany?.countryId as number,
    limit: APP_CONFIG.PAGE_LIMIT,
    orderBy: TaxReliefTypeOrderBy.NAME_ASC,
  });

  const handleClose = () => setAnchorEl(null);
  const handleOpenModal = () => {
    setAnchorEl(null);
    setIsEditForm(false);
    setOpenModal(true);
  };

  const { formDataValues, setFormData } = useEditFormStore();
  const [deleting, setDeleting] = useState(false);
  const { mutate: deleteSavingsScheme } = useDeleteSavingsScheme();
  const { reloadTableData } = useManageTableWithSearchParamsData();

  const handleDeleteMethod = () => {
    setDeleting(true);

    deleteSavingsScheme(
      { id: formDataValues?.id },
      {
        onSuccess: () => {
          reloadTableData();
          setOpenDeleteModal(false);
        },
        onSettled: () => setDeleting(false),
      },
    );
  };

  const additionalColumns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      minWidth: 150,
      flex: 1,
      headerClassName: "super-app-theme--header",
      cellClassName: "actions ",
      getActions: (params: any) => {
        return [
          <GridActionsCellItem
            icon={<BiDotsHorizontalRounded />}
            label="actions"
            onClick={(e: any) => {
              // console.log(params.row, "params");
              setFormData(params?.row);
              setAnchorEl(e.currentTarget);
            }}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mt-4 mb-8">
        <div className="flex items-center gap-3">
          <APISearchInput
            fallbackData={fetchedTableData}
            setTableData={setTableData}
            mutate={searchSavingsSchemes}
          />
          <TableFilter
            fields={[
              {
                title: "",
                fieldName: "",
                fieldType: "SORT",
                options: [
                  {
                    title: "Name",
                    fieldName: "name",
                  },
                  {
                    title: "Code",
                    fieldName: "code",
                  },
                  {
                    title: "Date Created",
                    fieldName: "createdAt",
                  },
                  {
                    title: "Date Modified",
                    fieldName: "modifiedAt",
                  },
                ],
              },
              {
                title: "Apply Employee Contribution On",
                fieldName: "applyEmployeeContributionOn",
                fieldType: "CHECKBOX_LIST",
                options: APPLY_SAVINGS_CONTRIBUTION_ON,
              },
              {
                title: "Apply Employer Contribution On",
                fieldName: "applyEmployerContributionOn",
                fieldType: "CHECKBOX_LIST",
                options: APPLY_SAVINGS_CONTRIBUTION_ON,
              },
              {
                title: "Prorate",
                fieldName: "prorate",
                fieldType: "YES_NO",
              },
              {
                title: "Statutory",
                fieldName: "statutory",
                fieldType: "YES_NO",
              },
            ]}
          />
          <ManageTableColumns
            columns={savingsColumns}
            defaultVisibilityModel={savingsColumnVisibility}
            visibilityModel={columnVisibilityModel}
            setVisibilityModel={setColumnVisibilityModel}
          />
        </div>
        <PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
          <div
            className="flex items-center text-primary-blue cursor-pointer"
            onClick={handleOpenModal}
          >
            <AiOutlinePlus className="mr-1 text-base" />
            <span className="text-sm">Add New Savings Scheme</span>
          </div>
        </PermissionChecker>
      </div>
      <TanStackEmployeeTable
        ref={tableAPIRef}
        tableId={TableId.SAVINGS}
        data={tableData}
        columns={savingsColumns}
        columnVisibility={columnVisibilityModel} // ✅ Add this
        onColumnVisibilityChange={setColumnVisibilityModel} // ✅ Add this
        loading={isLoading}
        onRowClick={
          checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
            ? (row, event) => {
                setFormData(row);
                setAnchorEl(event.currentTarget);
              }
            : undefined
        }
        onActionClick={(row, event) => {
          setFormData(row);
          setAnchorEl(event.currentTarget);
        }}
        initialColumnVisibility={savingsColumnVisibility}
        pageSize={10}
        hasPermission={checkUserPermissionsAnd([
          PERMISSIONS.COMPANY_CONFIGS_READ,
        ])}
        pageCount={pageCount}
        apiRef={tableAPIRef}
      />
      <ActionPopover
        id={anchorEl ? "simple-popover" : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        handleClose={handleClose}
        setOpenDeleteModal={setOpenDeleteModal}
        setOpenEdit={setIsEditForm}
        setOpenModal={setOpenModal}
      />
      <DeleteModal
        openDeleteModal={openDeleteModal}
        spanContent={formDataValues?.name || ""}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteMethod={handleDeleteMethod}
        deleting={deleting}
      />
      {openModal && (
        <SavingsModalForm
          open={openModal}
          setOpen={setOpenModal}
          title={
            isEditForm ? "Update Savings Scheme" : "Add New Savings Scheme"
          }
          isEditForm={isEditForm}
          tableAPIRef={tableAPIRef}
          payPeriods={payPeriods?.data || []}
          employees={employees?.data || []}
          employeeBands={employeeBands?.data || []}
          taxReliefTypes={taxReliefTypes?.data || []}
          reloadTableData={reloadTableData}
        />
      )}
    </>
  );
};

export default SavingsView;
